/**
 * Deterministic math helpers for the simulation.
 *
 * Only + - * / % and Math.floor/ceil/round/trunc/abs/min/max/sign/sqrt/imul are used,
 * which produce bit-identical results on every JS engine (see docs/TECHNICAL.md#determinism-rules).
 */

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** x^0.75 computed as sqrt(x) * sqrt(sqrt(x)) (deterministic across engines). */
export function pow075(x: number): number {
  if (!(x > 0)) return 0;
  const s = Math.sqrt(x);
  return s * Math.sqrt(s);
}

/** x^0.25 = sqrt(sqrt(x)). */
export function pow025(x: number): number {
  if (!(x > 0)) return 0;
  return Math.sqrt(Math.sqrt(x));
}

/** Integer power with a non-negative integer exponent (repeated multiplication). */
export function ipow(base: number, exp: number): number {
  let result = 1;
  let b = base;
  let e = exp | 0;
  while (e > 0) {
    if (e & 1) result *= b;
    b *= b;
    e >>= 1;
  }
  return result;
}

/** typeof number && finite. */
export function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/** Finite integer. */
export function isInteger(v: unknown): v is number {
  return typeof v === "number" && Number.isInteger(v);
}
